import React, { useState, useRef } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Card, CardBody, Button, Progress } from "reactstrap"
import {
  FaFileAlt,
  FaFilePdf,
  FaFileImage,
  FaFileWord,
  FaFileExcel,
  FaTimes,
  FaUpload,
  FaCheckCircle,
  FaArrowLeft,
} from "react-icons/fa"
import { MdError } from "react-icons/md"
import Breadcrumb from "components/Common/Breadcrumb"
import Loader from "components/Loader/loader"
import { axiosbaseurl } from "../../config"

// ── Helpers ────────────────────────────────────────────────────────
const getFileIcon = file => {
  const type = file.type
  if (type.includes("pdf"))
    return <FaFilePdf size={20} className="text-danger" />
  if (type.includes("image"))
    return <FaFileImage size={20} className="text-primary" />
  if (type.includes("word") || type.includes("document"))
    return <FaFileWord size={20} className="text-info" />
  if (type.includes("sheet") || type.includes("excel"))
    return <FaFileExcel size={20} className="text-success" />
  return <FaFileAlt size={20} className="text-secondary" />
}

const formatSize = bytes => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const initialFileState = file => ({
  file,
  status: "idle", // idle | uploading | done | error
  progress: 0,
  error: null,
})

// ── Page Component ─────────────────────────────────────────────────
const DocumentPage = () => {
  document.title = "Department Documents | Siddhi Tax Consultant"

  const { id } = useParams()

  const navigate = useNavigate()

  const [fileList, setFileList] = useState([])
  const [loading, setLoading] = useState(false)
  const inputRef = useRef()

  const handleFileSelect = e => {
    const selected = Array.from(e.target.files)
    setFileList(prev => [...prev, ...selected.map(initialFileState)])
    e.target.value = ""
  }

  const removeFile = index => {
    setFileList(prev => prev.filter((_, i) => i !== index))
  }

  const uploadSingle = async index => {
    const item = fileList[index]
    if (!item || item.status === "done" || item.status === "uploading") return

    const formData = new FormData()
    formData.append("file", item.file)

    setFileList(prev =>
      prev.map((f, i) =>
        i === index
          ? { ...f, status: "uploading", progress: 0, error: null }
          : f
      )
    )

    try {
      await axiosbaseurl.post(`project/file/${id}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: e => {
          const percent = Math.round((e.loaded * 100) / e.total)
          setFileList(prev =>
            prev.map((f, i) => (i === index ? { ...f, progress: percent } : f))
          )
        },
      })

      setFileList(prev =>
        prev.map((f, i) =>
          i === index ? { ...f, status: "done", progress: 100 } : f
        )
      )
    } catch (err) {
      setFileList(prev =>
        prev.map((f, i) =>
          i === index
            ? {
                ...f,
                status: "error",
                error: err?.response?.data?.message || "Upload failed",
              }
            : f
        )
      )
    }
  }

  const uploadAll = () => {
    fileList.forEach((item, index) => {
      if (item.status === "idle" || item.status === "error") {
        uploadSingle(index)
      }
    })
  }

  const clearPending = () => {
    setFileList(prev =>
      prev.filter(f => f.status === "done" || f.status === "uploading")
    )
  }

  const pendingCount = fileList.filter(
    f => f.status === "idle" || f.status === "error"
  ).length
  const doneCount = fileList.filter(f => f.status === "done").length
  const uploadingCount = fileList.filter(f => f.status === "uploading").length

  return (
    <div className="page-content">
      {loading && <Loader />}
      <Breadcrumb title="Departments" breadcrumbItem="Documents" />

      <Card className="shadow-lg">
        <CardBody>
          {/* ── Header Row ────────────────────────────────────── */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h5 className="fw-bold mb-0">Department Documents</h5>
              <small className="text-muted">Department ID: {id}</small>
            </div>
            <div className="d-flex gap-2 align-items-center">
              {doneCount > 0 && (
                <span className="badge bg-success px-3 py-3 fs-6">
                  <FaCheckCircle className="me-1" />
                  {doneCount} uploaded
                </span>
              )}
              {uploadingCount > 0 && (
                <span className="badge bg-primary px-3 py-2">
                  {uploadingCount} uploading...
                </span>
              )}
              {pendingCount > 0 && (
                <Button className="btn-blue rounded-4 px-3" onClick={uploadAll}>
                  <FaUpload className="me-2" size={13} />
                  Upload All ({pendingCount})
                </Button>
              )}
              {fileList.length > 0 && (
                <Button
                  color="primary"
                  outline
                  className="rounded-4 px-3"
                  onClick={() => inputRef.current.click()}
                >
                  <FaUpload className="me-2" size={13} />
                  Select Files
                </Button>
              )}

              {/* <Button
                color="secondary"
                outline
                className="rounded-4 px-3"
                onClick={() => navigate(-1)}
              >
                <FaArrowLeft className="me-2" size={13} />
                Back
              </Button> */}

              <input
                ref={inputRef}
                type="file"
                multiple
                style={{ display: "none" }}
                onChange={handleFileSelect}
              />
            </div>
          </div>

          {/* ── Drop Zone (shown only when no files) ──────────── */}
          {fileList.length === 0 && (
            <div
              onClick={() => inputRef.current.click()}
              style={{
                border: "2px dashed #0d6efd",
                borderRadius: "0.75rem",
                padding: "3rem 2rem",
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: "#f0f6ff",
                transition: "background 0.2s",
              }}
              onMouseEnter={e =>
                (e.currentTarget.style.backgroundColor = "#ddeeff")
              }
              onMouseLeave={e =>
                (e.currentTarget.style.backgroundColor = "#f0f6ff")
              }
            >
              <FaUpload size={36} className="text-primary mb-3" />
              <p className="mb-1 fw-semibold text-primary fs-5">
                Click to select files
              </p>
              <p className="mb-0 text-muted">
                All file types supported — PDF, Images, Word, Excel, ZIP, and
                more
              </p>
            </div>
          )}

          {/* ── File List ──────────────────────────────────────── */}
          {fileList.length > 0 && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="fw-semibold text-muted">
                  {fileList.length} file{fileList.length > 1 ? "s" : ""}{" "}
                  selected
                </span>
                {pendingCount > 0 && (
                  <small
                    className="text-danger"
                    style={{ cursor: "pointer" }}
                    onClick={clearPending}
                  >
                    Clear pending
                  </small>
                )}
              </div>

              <div className="d-flex flex-column gap-2">
                {fileList.map((item, index) => (
                  <div
                    key={index}
                    className="d-flex align-items-start gap-3 p-3 rounded-3"
                    style={{
                      border: "1px solid",
                      borderColor:
                        item.status === "done"
                          ? "#198754"
                          : item.status === "error"
                          ? "#dc3545"
                          : item.status === "uploading"
                          ? "#0d6efd"
                          : "#dee2e6",
                      backgroundColor:
                        item.status === "done"
                          ? "#f0fff4"
                          : item.status === "error"
                          ? "#fff5f5"
                          : item.status === "uploading"
                          ? "#f0f6ff"
                          : "#fff",
                    }}
                  >
                    {/* File Icon */}
                    <div className="pt-1">{getFileIcon(item.file)}</div>

                    {/* Info + Progress */}
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <div className="d-flex justify-content-between align-items-center">
                        <span
                          className="fw-medium text-truncate"
                          style={{ fontSize: 14, maxWidth: "75%" }}
                          title={item.file.name}
                        >
                          {item.file.name}
                        </span>
                        <span className="text-muted" style={{ fontSize: 12 }}>
                          {formatSize(item.file.size)}
                        </span>
                      </div>

                      {item.status === "uploading" && (
                        <div className="mt-2">
                          <Progress
                            value={item.progress}
                            color="primary"
                            style={{ height: 6, borderRadius: 4 }}
                          />
                          <small className="text-primary">
                            {item.progress}% uploading...
                          </small>
                        </div>
                      )}

                      {item.status === "done" && (
                        <div className="d-flex align-items-center gap-1 mt-1">
                          <FaCheckCircle size={13} className="text-success" />
                          <small className="text-success">
                            Uploaded successfully
                          </small>
                        </div>
                      )}

                      {item.status === "error" && (
                        <div className="d-flex align-items-center gap-1 mt-1">
                          <MdError size={14} className="text-danger" />
                          <small className="text-danger">{item.error}</small>
                        </div>
                      )}

                      {item.status === "idle" && (
                        <small className="text-muted">Ready to upload</small>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="d-flex align-items-center gap-2 pt-1">
                      {item.status !== "uploading" && (
                        <button
                          title="Remove"
                          onClick={() => removeFile(index)}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: 0,
                            color: "#aaa",
                          }}
                        >
                          <FaTimes size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardBody>
      </Card>
    </div>
  )
}

export default DocumentPage
